import {
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { Crop, Move, RotateCcw, ZoomIn } from 'lucide-react';
import { DEFAULT_IMAGE_CROP, normalizeImageCrop } from '../../content/imageCrop';
import type { ImageCrop } from '../../content/types';

type Props = {
  open: boolean;
  src: string;
  label: string;
  aspectRatio: number;
  value?: ImageCrop | null;
  defaultValue?: ImageCrop;
  onClose: () => void;
  onApply: (crop: ImageCrop | null) => void;
};

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));
const AUTO_PAN_ZOOM = 1.2;

function normalizedOrDefault(value: ImageCrop | null | undefined, fallback: ImageCrop) {
  return normalizeImageCrop(value) ?? { ...fallback };
}

export function ImageCropModal({
  open,
  src,
  label,
  aspectRatio,
  value,
  defaultValue = DEFAULT_IMAGE_CROP,
  onClose,
  onApply,
}: Props) {
  const titleId = useId();
  const descriptionId = useId();
  const [draft, setDraft] = useState<ImageCrop>(() => normalizedOrDefault(value, defaultValue));
  const [usesPlacementDefault, setUsesPlacementDefault] = useState(!normalizeImageCrop(value));
  const [dragging, setDragging] = useState(false);
  const [imageSize, setImageSize] = useState<{ width: number; height: number } | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    crop: ImageCrop;
  } | null>(null);

  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  useEffect(() => {
    if (!open) return;
    const normalized = normalizeImageCrop(value);
    setDraft(normalized ?? { ...defaultValue });
    setUsesPlacementDefault(!normalized);
    setDragging(false);
    setImageSize(null);
    dragRef.current = null;
  }, [defaultValue.x, defaultValue.y, defaultValue.zoom, open, src, value]);

  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const focusTimer = window.setTimeout(() => closeRef.current?.focus(), 0);
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>(
        'button:not(:disabled), input:not(:disabled), [href], [tabindex]:not([tabindex="-1"])',
      ) ?? []);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', onKeyDown);
      previouslyFocused?.focus();
    };
  }, [open]);

  if (!open) return null;

  const updateDraft = (patch: Partial<ImageCrop>) => {
    setUsesPlacementDefault(false);
    setDraft((current) => normalizedOrDefault({ ...current, ...patch }, defaultValue));
  };

  const reset = () => {
    setDraft({ ...defaultValue });
    setUsesPlacementDefault(true);
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      crop: draft,
    };
    setDragging(true);
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const rect = event.currentTarget.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const naturalWidth = imageSize?.width ?? rect.width;
    const naturalHeight = imageSize?.height ?? rect.height;
    const coverScale = Math.max(rect.width / naturalWidth, rect.height / naturalHeight);
    const coveredWidth = naturalWidth * coverScale;
    const coveredHeight = naturalHeight * coverScale;
    const deltaX = event.clientX - drag.startX;
    const deltaY = event.clientY - drag.startY;
    if (Math.abs(deltaX) < 0.5 && Math.abs(deltaY) < 0.5) return;

    const overflowAt = (coveredSize: number, viewportSize: number, zoom: number) => Math.max(0, coveredSize * zoom - viewportSize);
    const zoomNeededForAxis = (delta: number, start: number, coveredSize: number, viewportSize: number) => {
      if (Math.abs(delta) < 0.5) return drag.crop.zoom;
      const overflow = overflowAt(coveredSize, viewportSize, drag.crop.zoom);
      // Images that already have room should stop naturally at their edge. Only
      // exact-fit axes need an automatic zoom to make dragging possible.
      if (overflow > 0.5) return drag.crop.zoom;
      const availablePercent = delta < 0 ? 100 - start : start;
      if (availablePercent <= 0.5) return drag.crop.zoom;
      const requiredOverflow = (Math.abs(delta) * 100) / availablePercent;
      return (viewportSize + requiredOverflow) / coveredSize;
    };

    const horizontalZoom = zoomNeededForAxis(deltaX, drag.crop.x, coveredWidth, rect.width);
    const verticalZoom = zoomNeededForAxis(deltaY, drag.crop.y, coveredHeight, rect.height);
    const needsAutomaticRoom = horizontalZoom > drag.crop.zoom + 0.001 || verticalZoom > drag.crop.zoom + 0.001;
    const effectiveZoom = clamp(Math.max(
      drag.crop.zoom,
      horizontalZoom,
      verticalZoom,
      needsAutomaticRoom ? AUTO_PAN_ZOOM : drag.crop.zoom,
    ), 1, 3);
    const horizontalOverflow = overflowAt(coveredWidth, rect.width, effectiveZoom);
    const verticalOverflow = overflowAt(coveredHeight, rect.height, effectiveZoom);
    updateDraft({
      x: horizontalOverflow > 0.5
        ? clamp(drag.crop.x - (deltaX / horizontalOverflow) * 100, 0, 100)
        : drag.crop.x,
      y: verticalOverflow > 0.5
        ? clamp(drag.crop.y - (deltaY / verticalOverflow) * 100, 0, 100)
        : drag.crop.y,
      zoom: effectiveZoom,
    });
  };

  const endDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (dragRef.current?.pointerId !== event.pointerId) return;
    dragRef.current = null;
    setDragging(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  };

  const onPreviewKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey ? 8 : 2;
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
    event.preventDefault();
    const rect = event.currentTarget.getBoundingClientRect();
    const naturalWidth = imageSize?.width ?? rect.width;
    const naturalHeight = imageSize?.height ?? rect.height;
    const coverScale = rect.width && rect.height
      ? Math.max(rect.width / naturalWidth, rect.height / naturalHeight)
      : 1;
    const horizontalOverflow = naturalWidth * coverScale * draft.zoom - rect.width;
    const verticalOverflow = naturalHeight * coverScale * draft.zoom - rect.height;
    const movesHorizontally = event.key === 'ArrowLeft' || event.key === 'ArrowRight';
    const movesVertically = event.key === 'ArrowUp' || event.key === 'ArrowDown';
    const needsAutomaticRoom = (movesHorizontally && horizontalOverflow <= 0.5)
      || (movesVertically && verticalOverflow <= 0.5);
    const patch: Partial<ImageCrop> = needsAutomaticRoom
      ? { zoom: Math.max(draft.zoom, AUTO_PAN_ZOOM) }
      : {};
    if (event.key === 'ArrowLeft') patch.x = clamp(draft.x - step, 0, 100);
    if (event.key === 'ArrowRight') patch.x = clamp(draft.x + step, 0, 100);
    if (event.key === 'ArrowUp') patch.y = clamp(draft.y - step, 0, 100);
    if (event.key === 'ArrowDown') patch.y = clamp(draft.y + step, 0, 100);
    updateDraft(patch);
  };

  return (
    <div className="ed-modal-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div
        ref={dialogRef}
        className="ed-modal ed-crop-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
      >
        <header className="ed-modal-head">
          <div className="ed-crop-title"><span><Crop size={17} /></span><div><h2 id={titleId}>Adjust crop</h2><small>{label}</small></div></div>
          <button ref={closeRef} type="button" className="ed-modal-x" onClick={onClose} aria-label="Close crop editor">✕</button>
        </header>

        <div className="ed-crop-body">
          <div className="ed-crop-canvas-wrap">
            <div
              className={`ed-crop-canvas ${dragging ? 'is-dragging' : ''}`}
              style={{ aspectRatio, '--ed-crop-aspect': aspectRatio } as CSSProperties}
              role="group"
              aria-roledescription="image crop area"
              tabIndex={0}
              aria-label={`Crop preview for ${label}. Drag the image or use the arrow keys to reposition it.`}
              onKeyDown={onPreviewKeyDown}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
            >
              <img
                src={src}
                alt=""
                draggable={false}
                onLoad={(event) => setImageSize({ width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight })}
                style={{
                  objectPosition: `${draft.x}% ${draft.y}%`,
                  transform: `scale(${draft.zoom})`,
                  transformOrigin: `${draft.x}% ${draft.y}%`,
                }}
              />
              <span className="ed-crop-grid" aria-hidden><i /><i /><i /><i /></span>
              <span className="ed-crop-move-hint" aria-hidden><Move size={16} />Drag to reposition</span>
            </div>
          </div>

          <aside className="ed-crop-controls">
            <div className="ed-crop-help" id={descriptionId}>
              <span><Crop size={18} /></span>
              <p><b>Crop for this placement</b>Just drag to reposition. If the image exactly fits, the editor automatically creates enough room to move it.</p>
            </div>

            <label className="ed-crop-zoom">
              <span><ZoomIn size={16} />Zoom <output>{draft.zoom.toFixed(2)}×</output></span>
              <input
                type="range"
                min="1"
                max="3"
                step="0.05"
                value={draft.zoom}
                aria-label="Image zoom"
                onChange={(event) => updateDraft({ zoom: Number(event.target.value) })}
              />
            </label>

            <div className="ed-crop-position">
              <span>Horizontal <b>{Math.round(draft.x)}%</b></span>
              <span>Vertical <b>{Math.round(draft.y)}%</b></span>
            </div>

            <p className="ed-crop-keys">The original image stays unchanged. Use arrow keys for precise positioning; hold Shift for larger steps.</p>
            <button type="button" className="ed-crop-reset" onClick={reset}><RotateCcw size={15} />Reset to placement default</button>
          </aside>
        </div>

        <footer className="ed-modal-foot ed-crop-foot">
          <span>{usesPlacementDefault ? 'Using the placement default' : 'Custom crop ready to apply'}</span>
          <div>
            <button type="button" className="ed-btn" onClick={onClose}>Cancel</button>
            <button type="button" className="ed-btn ed-btn-primary" onClick={() => { onApply(usesPlacementDefault ? null : draft); onClose(); }}>Apply crop</button>
          </div>
        </footer>
      </div>
    </div>
  );
}
